import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { useQuery, useLazyQuery } from "@apollo/client";
import {
  downloadExcel,
  downloadKML,
  downloadPDF,
} from "../../../utils/generalUtils";

import Structure from "../../../components/Base/Structure";

import ASSETS_BYGROUPS_QUERY from "../../../graphql/queries/assetByGroupsQuery";
import DRIVERS_PAGINATED_QUERY from "../../../graphql/queries/getDriversPaginatedQuery";
import GET_EVENTREPORT_QUERY from "../../../graphql/queries/getEventReportQuery";

import EVENTREPORT_EXPORT_QUERY from "../../../graphql/queries/getEventReportExportQuery";
import TYPE_EVENTALL_QUERY from "../../../graphql/queries/typeEventAllQuery";

import Filters from "./Filters";
import ReportResult from "./ReportResult";

import { Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";

import PageTitle from "../../../components/Base/PageTitle";
import { useFilterValidation } from "../useFilterValidation";

import { useTranslation } from "../../../lang";
import { localDateTimeStringToUTC } from "../../../utils/dates";

const EventReport = ({ selectedGroups, user }) => {
  const [t] = useTranslation();
  const [assetList, setAssetList] = useState([]);
  const [exportSelected, setExportSelected] = useState("EXCEL");
  const [driverList, setDriverList] = useState([]);
  const [typeEventList, setTypeEventList] = useState([]);
  const [assetTableState, setAssetTableState] = useState({
    searchText: "",
    sort: undefined,
    paginate: false,
  });

  const [reportResult, setReportResult] = useState([]);

  const [resultState, setResultState] = useState({
    isOpen: false,
    type: "success",
    msg: "",
  });

  const {} = useQuery(ASSETS_BYGROUPS_QUERY, {
    fetchPolicy: "network-only",
    variables: {
      groupIDs: selectedGroups.filter(
        (g) => g && !Number.isNaN(Number.parseInt(g))
      ),
      ...assetTableState,
    },
    onCompleted(d) {
      const { count, items } = d.assetsByGroups;
      //setTotal(count);
      setAssetList(items.map((a) => ({
        ...a,
        completeName: `${a.name} - ${a.licensePlate}`
      })));
    },
  });

  const {} = useQuery(DRIVERS_PAGINATED_QUERY, {
    fetchPolicy: "network-only",
    variables: {
      paginate: false,
    },
    onCompleted(d) {
      setDriverList(d.driversPaginated.items);
    },
  });

  const {} = useQuery(TYPE_EVENTALL_QUERY, {
    onCompleted(d) {
      setTypeEventList([{ label: t("Todos"), value: -1 }, ...d.typeEventAll]);
    },
  });

  const [fetchReport] = useLazyQuery(GET_EVENTREPORT_QUERY, {
    fetchPolicy: "network-only",
    onCompleted(d) {
      if (d.eventReport) setReportResult(d.eventReport);
      else {
        setReportResult([]);
        setResultState({
          isOpen: true,
          type: "info",
          msg: t("No se encontraron datos para su consulta"),
        });
      }
    },
    onError(err) {
      setResultState({
        isOpen: true,
        type: "error",
        msg: err.message,
      });
    },
  });

  const [getExport] = useLazyQuery(EVENTREPORT_EXPORT_QUERY, {
    fetchPolicy: "network-only",
    onCompleted(d) {
      switch (exportSelected) {
        case "EXCEL":
          downloadExcel(d.eventReportExport, `${t("ReporteEventos")}`);
          break;
        case "PDF":
          downloadPDF(d.eventReportExport, `${t("ReporteEventos")}`);
          break;
        case "KML":
          downloadKML(d.eventReportExport, `${t("ReporteEventos")}`);
          break;
      }
    },
  });

  // Translate "All/Todos" option when user changes language
  useEffect(() => {
    if (typeEventList && typeEventList.length > 0) {
      const newTypeEventList = [...typeEventList];
      newTypeEventList.shift();

      setTypeEventList(
        typeEventList.length === 1
          ? typeEventList
          : [{ label: t("Todos"), value: -1 }, ...newTypeEventList]
      );
    }
  }, [user.language]);

  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setResultState({
      ...resultState,
      isOpen: false,
    });
  };

  const handleSearchReportEvents = ({
    assetsSelected,
    driversSelected,
    typeEventSelected,
    fechaDesde,
    fechaHasta,
    dias,
  }) => {
    fetchReport({
      variables: {
        assetsSelected: assetsSelected,
        driversSelected: driversSelected,
        typeEventSelected:
          !typeEventSelected || typeEventSelected.includes(-1)
            ? undefined
            : typeEventSelected,
        fechaInicio: localDateTimeStringToUTC(fechaDesde),
        fechaFin: localDateTimeStringToUTC(fechaHasta),
        days: dias,
      },
    });
  };

  const { handleSearchReport, errors: filterErrors } = useFilterValidation(
    handleSearchReportEvents
  );
  const handleExportExcel = (
    assetsSelected,
    driversSelected,
    typeEventSelected,
    fechaDesde,
    fechaHasta,
    dias
  ) => {
    setExportSelected("EXCEL");
    getExport({
      variables: {
        assetsSelected: assetsSelected,
        driversSelected: driversSelected,
        typeEventSelected:
          !typeEventSelected || typeEventSelected.includes(-1)
            ? undefined
            : typeEventSelected,
        fechaInicio: localDateTimeStringToUTC(fechaDesde),
        fechaFin: localDateTimeStringToUTC(fechaHasta),
        days: dias,
        format: "EXCEL",
      },
    });
  };

  const handleExportPDF = (
    assetsSelected,
    driversSelected,
    typeEventSelected,
    fechaDesde,
    fechaHasta,
    dias
  ) => {
    setExportSelected("PDF");
    getExport({
      variables: {
        assetsSelected: assetsSelected,
        driversSelected: driversSelected,
        typeEventSelected:
          !typeEventSelected || typeEventSelected.includes(-1)
            ? undefined
            : typeEventSelected,
        fechaInicio: localDateTimeStringToUTC(fechaDesde),
        fechaFin: localDateTimeStringToUTC(fechaHasta),
        days: dias,
        format: "PDF",
      },
    });
  };

  const handleExportKML = (
    assetsSelected,
    driversSelected,
    typeEventSelected,
    fechaDesde,
    fechaHasta,
    dias
  ) => {
    setExportSelected("KML");
    getExport({
      variables: {
        assetsSelected: assetsSelected,
        driversSelected: driversSelected,
        typeEventSelected:
          !typeEventSelected || typeEventSelected.includes(-1)
            ? undefined
            : typeEventSelected,
        fechaInicio: localDateTimeStringToUTC(fechaDesde),
        fechaFin: localDateTimeStringToUTC(fechaHasta),
        days: dias,
        format: "KML",
      },
    });
  };

  return (
    <Structure>
      <Snackbar
        open={resultState.isOpen}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseNotification}
          severity={resultState.type}
          variant="filled"
        >
          {resultState.msg}
        </MuiAlert>
      </Snackbar>
      <PageTitle sector={t("Estadísticas")} title={t("Eventos")} />
      <Filters
        assets={assetList}
        drivers={driverList}
        typeEventList={typeEventList}
        searchReport={handleSearchReport}
        handleExportExcel={handleExportExcel}
        handleExportPDF={handleExportPDF}
        handleExportKML={handleExportKML}
        errors={filterErrors}
        reportResult={Array.isArray(reportResult) && reportResult.length > 0}
        user={user}
      />
      {Array.isArray(reportResult) && reportResult.length > 0 && (
        <ReportResult reportData={reportResult} />
      )}
    </Structure>
  );
};

const _mapStateToProps = ({ groups, user }) => {
  return {
    selectedGroups: groups.selectedGroups,
    user,
  };
};

export default connect(_mapStateToProps, null)(EventReport);
